import { IDTokenClaims, User, UserManager, UserManagerSettings, WebStorageStateStore } from "oidc-client"

export interface AccessTokenClaims {
  nbf: number;
  exp: number;
  iss: string;
  client_id: string;
  sub: string;
  auth_time: number;
  idp: string;
  id: string;
  name: string;
  email: string;
  organization_id: string;
  default_region: string;
  default_provider: string;
  permissions: ReadonlyArray<string>;
  jti: string;
  iat: number;
  scope: ReadonlyArray<string>;
  amr: ReadonlyArray<string>;

}

type UserManangerCore = {
  [P in keyof UserManager]: UserManager[P]
}

interface TokenClient {
  exchangeRefreshToken(x:{ refresh_token: string }): Promise<{
    access_token: string;
    id_token: string;
    refresh_token: string;
    scope: string;
    token_type: string;
    expires_in: number;
  }>;
}

export type CastedUserManager = UserManangerCore & {
  _joseUtil: JoseUtil;
  _useRefreshToken(x:{ refresh_token: string }): Promise<User | null>;
  _tokenClient: TokenClient;
  _userStoreKey: string;
  _inspectRefreshedStatus: () => Promise<void>
}

type HashAlg =
  'sha256' | 'sha384' | 'sha512'

interface JwtHeader {
  kid: string;
  alg: string;
  x5t: string;
  typ: 'jwt';
}

export interface JoseUtil {
  parseJwt<T = IDTokenClaims>(jwt:string): { header: JwtHeader, payload: T };
  validateJwt: (
    jwt: string,
    key: any,
    issuer: string,
    audience: string,
    clockSkew?: number,
    now?: number,
    timeInsensitive?: boolean) => Promise<IDTokenClaims>;
  validateJwtAttributes: (
    jwt: string,
    issuer: string,
    audience: string,
    clockSkew?: number,
    now?: number,
    timeInsensitive?: boolean) => Promise<IDTokenClaims>;
  hashString: (value:string, alg: HashAlg) => string;
  hexToBase64Url: (value:string) => string;
}

const settings: UserManagerSettings = {
  authority: import.meta.env.VITE_BACKEND_USER_API_BASEURL || "https://api.ondatahub.com/users",
  client_id: "ondata-portal",
  automaticSilentRenew: false,
  userStore: new WebStorageStateStore({}), // defaults to use localstorage
}

export const userManager = new UserManager(settings);


// silent token refresh using a bidding algorithm, for multiple tabs scenario

(() => {
  
interface BidMsg        { kind: 'bid', price: number, at: number }
interface ClaimMsg      { kind: 'claimWinner', at: number }
interface ReportSuccess { kind: 'reportSuccess' }
interface ReportError   { kind: 'reportError', grantErr: boolean, at: number }

interface KeepPositive { kind: 'keep_positive' }

type Message = 
| BidMsg
| ClaimMsg
| ReportSuccess
| ReportError
| KeepPositive


const billboard = {
    bids: [] as BidMsg[],
    claim: null as ClaimMsg|null,
    fails: [] as ReportError[],
}
let timerB : number|null = null
let timerC : number|null = null

const Logger = (() => {
  const on = false
  return on 
    ? { log: console.log.bind(console), warn: console.warn.bind(console), error: console.error.bind(console) } 
    : { log: () => {}, warn: () => {}, error: () => {} }
})()

const channel = new BroadcastChannel('auth')
channel.onmessage = (ev) => {
  if (!ev.data || typeof ev.data.kind !== 'string') { return }
  const msg: Message = ev.data
  Logger.log(`msg: #${JSON.stringify(msg)}#`)
  switch (msg.kind) {
    case 'bid': {
      if (billboard.claim) {
        // someone already claims the winner, obsoletes all following bids
        break;
      }
      billboard.bids.push(msg);
      timerB && window.clearTimeout(timerB)
      timerB = window.setTimeout(() => { billboard.bids.length = 0 }, 1.2 * 60 * 1000)
      break;
    }
    case 'claimWinner': {
      if (billboard.claim) {
        Logger.warn(`whats going on?! ${new Date().toISOString()}`)
      }
      billboard.claim = msg
      billboard.bids.length = 0
      // the claimed winner should refresh and report result within 15s
      timerC && window.clearTimeout(timerC)
      timerC = window.setTimeout(() => {
        if (billboard.claim === msg) {
          billboard.claim = null
          billboard.bids.length = 0
          timerC = null
        }
      }, msg.at + 15 * 1000 - Date.now())
      clearTimerB()
      break;
    }
    case 'reportSuccess': {
      billboard.claim = null
      billboard.bids.length = 0
      billboard.fails.length = 0
      clearTimerB()
      clearTimerC()
      refreshSettled()
      break;
    }
    case 'reportError': {
      billboard.claim = null
      billboard.bids.length = 0
      if (msg.grantErr) {
        if (billboard.fails.length === 0 || billboard.fails[0].at + 5000 < msg.at) {
          billboard.fails.unshift(msg)
        }
        if (billboard.fails.length >= 6) {
          const tokens = loadTokens()
          if (shouldRefreshToken(tokens)) {
            localStorage.removeItem(KEY)
          }  
          billboard.fails.length = 0
        }
      }
      clearTimerB()
      clearTimerC()
      refreshSettled()
      break;
    }
    case 'keep_positive': {
      keepPositiveBeep = Math.round(new Date().getTime() / 1000)
      Logger.log(`keep_positive before ${new Date((keepPositiveBeep + KEEP_POSITIVE_MINS * 60) * 1000)}`)
      break;
    }

    default: break;
  }
}
const clearTimerB = () => {
  //@ts-ignore
  timerB && (window.clearTimeout(timerB), timerB = null) // eslint-disable-line
}
const clearTimerC = () => {
  //@ts-ignore
  timerC && (window.clearTimeout(timerC), timerC = null) // eslint-disable-line
}
let keepPositiveBeep = Math.round(new Date().getTime() / 1000)
const KEEP_POSITIVE_MINS = 30, REFRESH_AHEAD_MINS = 5

// We need these assumptions for the code to work:
// 
//#Assumption 1:
//   All tabs share the same system clock, they get a synchronized system clock view
//#Assumption 2:
//   For any tab A, it sends a broadcast message M at Tick(0); then for any tab B, it's guarented
//   to have recieved and handled the M by the time Tick(0+X)
//   
//#Assumption 3:
//   This X is of value 30 (miliseconds)
//   
//   ?? what about when all tabs are put to sleep, when setTimeout cadence is resetted to 5s by browser??
//   

const X = 30

let ongoing = false

const computeTimeLeft = (refreshTimeBeforeTokensExpirationInSecond: number, expiresAt: number) => {
  const currentTimeUnixSecond = new Date().getTime() / 1000;
  return Math.round(((expiresAt - refreshTimeBeforeTokensExpirationInSecond) - currentTimeUnixSecond));
};
const shouldRefreshToken = (t:User|null) => 
  t && computeTimeLeft(REFRESH_AHEAD_MINS * 60, t.expires_at) < 0 
    && computeTimeLeft(0, keepPositiveBeep + KEEP_POSITIVE_MINS * 60) > 0
    
const performPeriodicalRefresh = async () => {
  let myBidMsg = 'undecided' as BidMsg | 'giveup' | 'undecided'
  let tokens = loadTokens()
  if (!tokens || ongoing) { return }
  ongoing = true

  try {
    while (
      shouldRefreshToken(tokens)  // token is about to expire (in < 5mins)
      && (!billboard.claim)       // the winner has not been claimed
      && (myBidMsg !== 'giveup')  // and we didn't giveup bidding
    ) {

      if (myBidMsg === 'undecided') {
        if (billboard.bids.length === 0) {
          myBidMsg = { kind: 'bid', price: Math.random(), at: Date.now() }
          channel.postMessage(myBidMsg)

          // a valid bidding period is within the range of 10 milisecs after the first bid
          // we wait for 75 (=X*2.5) milisecs till all bid attempts settled
          await sleepMilis(X * 2.5)
          tokens = loadTokens()
        } else {
          myBidMsg = 'giveup'
          break;
        }
      } else {
        const myBid = myBidMsg

        if (billboard.bids.length === 0) {
          await claimAndRefresh()
          break;

        } else {
          const firstAt = Math.min(...billboard.bids.map(msg => msg.at))
          if (firstAt > myBid.at + 10) {
            await claimAndRefresh()
            break;
          } else if (firstAt + 10 < myBid.at) { // bid too late
            myBidMsg = 'giveup'
            break;
          }

          // we are here, means we are also the valid bid; then compare price

          const validBids = billboard.bids.filter(msg => msg.at < firstAt + 10)
          const maxPrice = Math.max(...validBids.map(msg => msg.price))
          if (maxPrice < myBid.price) {
            await claimAndRefresh()
            break;
          } else if (maxPrice > myBid.price) {
            myBidMsg = 'giveup'
            break;
          }

          // we are here, means we are also the power bidder; then compare bid time

          const powerBidders = validBids.filter(msg => msg.price === maxPrice)
          if (powerBidders.some(msg => msg.at < myBid.at)) {
            myBidMsg = 'giveup'
            break;
          } else if (powerBidders.some(msg => msg.at === myBid.at)) {
            // rebid!!
            billboard.claim = null
            billboard.bids.length = 0
            myBidMsg = 'undecided'
            tokens = loadTokens()
          } else {
            await claimAndRefresh()
            break;
          }
        }
      }
    }

    if (myBidMsg !== 'undecided') { Logger.log(`${Date()} - ${JSON.stringify(myBidMsg)}`) }
  } finally {
    ongoing = false
  }
}

// on page initialization, immediately detect and refresh for once
setTimeout(performPeriodicalRefresh, X)

// setup periodical refresh
setInterval(performPeriodicalRefresh, 5 * 1000)

const claimAndRefresh = async () => {
  clearTimerB()
  const tokens_0 = loadTokens()
  if (!shouldRefreshToken(tokens_0)) {
    Logger.log('was about to claim, but seems tokens is fine, no need to refresh')
    channel.postMessage({ kind: '_info', content: 'was about to claim, but...', url: window.location.pathname })

    billboard.claim = null
    billboard.bids.length = 0
    refreshSettled()
    return
  }
  try {
    const claimMsg: ClaimMsg = { kind: 'claimWinner', at: Date.now(), ...({ url: window.location.pathname } as any) }
    channel.postMessage(claimMsg)
    billboard.claim = claimMsg
    billboard.bids.length = 0
    Logger.log(`i won!!!!! - ${window.location.pathname}`)
    
    await (userManager as CastedUserManager)._useRefreshToken({ refresh_token: tokens_0!.refresh_token as any })

    Logger.log('token stored.')
    channel.postMessage({ kind: 'reportSuccess' })
    billboard.fails.length = 0
  } catch (err) {
    const errMsg : string|undefined = (err as any)?.message
    Logger.log(`token request failed. - ${errMsg}`)
    const reportErrMsg : ReportError = { kind: 'reportError', grantErr: errMsg === 'invalid_grant', at: Date.now() }
    channel.postMessage(reportErrMsg)

    if (reportErrMsg.grantErr) {
      billboard.fails.unshift(reportErrMsg)
      if (billboard.fails.length >= 6) {
        setTimeout(() => {
          const tokens = loadTokens()
          if (shouldRefreshToken(tokens)) {
            localStorage.removeItem(KEY)
          }  
          billboard.fails.length = 0
        })
      }
    }
  }
  billboard.claim = null
  billboard.bids.length = 0
  refreshSettled()
}

const sleepMilis = (n:number) => new Promise((resolve, reject) => setTimeout(resolve, n))

const KEY = "oidc." + (userManager as CastedUserManager)._userStoreKey

const loadTokens = () => {
  const str = localStorage.getItem(KEY)
  return str ? User.fromStorageString(str) : null
}

const inspectionPoint = {
  promise: null as Promise<void>|null,
  stub: null as { resolve:(x?:any)=>void, reject:(x?:any)=>void }|null,
}
const refreshSettled = async () => {
  if (!inspectionPoint.promise || !inspectionPoint.stub) { return }
  const user = await userManager.getUser()
  if (user && !user.expired) {
    inspectionPoint.stub?.resolve()
  } else {
    inspectionPoint.stub?.reject()
  }
  delete (inspectionPoint.stub as any).resolve
  delete (inspectionPoint.stub as any).reject
  inspectionPoint.stub = null
  inspectionPoint.promise = null
}
const inspectRefreshedStatus = () => {
  if (inspectionPoint.promise) { return inspectionPoint.promise }

  inspectionPoint.promise = new Promise((resolve, reject) => {
    inspectionPoint.stub = { resolve, reject }
  })
  return inspectionPoint.promise
}
(userManager as CastedUserManager)._inspectRefreshedStatus = inspectRefreshedStatus;

})();
